import { noop, find } from 'lodash';
import api from '../api';
import isEqualResource from '../utils/isEqualResource';
import eventsReporter from '../utils/events/reporter';
import { EventTypes } from '../utils/events/types';
import { MAX_COLLECTION_ITEMS } from '../utils/constants';
import { ValidationErrorType } from '../types/validationErrorType';
import { getCollection } from '../selectors';

export const CREATE_COLLECTION = 'CREATE_COLLECTION';
export const createCollection = ({ name, resource, onUpdateCollections = noop }) => async(dispatch, getState) => {
    const { items, collection } = await api.createCollection(name, resource);
    const item = items[0];

    await dispatch({
        type: CREATE_COLLECTION,
        data: {
            id: collection.id,
            name,
            items: [{
                id: item.id,
                resourceId: resource.id,
                resourceType: resource.type
            }]
        }
    });

    onUpdateCollections(getState().collections);

    eventsReporter.report(EventTypes.collection.create.success, { collection });
    eventsReporter.report(EventTypes.collection.item.create.success, { collection, item });
};

export const CREATE_COLLECTION_ITEM = 'CREATE_COLLECTION_ITEM';
export const createCollectionItem = ({ id, resource, onUpdateCollections = noop }) => async(dispatch, getState) => {
    const collection = getCollection(getState(), id);
    if (collection.items.length >= MAX_COLLECTION_ITEMS) {
        eventsReporter.report(EventTypes.collection.item.create.too_many_items);
        const error = new Error(`Collection ${id} has too many items. The limit is ${MAX_COLLECTION_ITEMS}`);
        error.key = ValidationErrorType.TOO_MANY_ITEMS;
        throw error;
    }

    const { item } = await api.createCollectionItem(id, resource);

    await dispatch({
        type: CREATE_COLLECTION_ITEM,
        data: {
            id,
            item: {
                id: item.id,
                resourceId: resource.id,
                resourceType: resource.type
            }
        }
    });

    onUpdateCollections(getState().collections);

    eventsReporter.report(EventTypes.collection.item.create.success, {
        collection,
        item
    });
};

export const DELETE_COLLECTION_ITEM = 'DELETE_COLLECTION_ITEM';
export const deleteCollectionItem = ({ id, itemId, onUpdateCollections = noop }) => async(dispatch, getState) => {
    const { items } = await api.deleteCollectionsItems([itemId]);
    const [item] = items;

    await dispatch({
        type: DELETE_COLLECTION_ITEM,
        data: {
            id,
            item
        }
    });

    const { collections } = getState();

    onUpdateCollections(collections);

    eventsReporter.report(EventTypes.collection.item.delete.success, {
        collection: find(collections, { id }),
        item
    });
};

export const DELETE_FROM_ALL_COLLECTIONS = 'DELETE_FROM_ALL_COLLECTIONS';
export const deleteFromAllCollections = ({ resource, onUpdateCollections = noop }) => async(dispatch, getState) => {
    const { collections } = getState();

    const collectionItemMap = getCollectionItemMap(collections, resource);
    const itemIds = Object.values(collectionItemMap).map(({ id }) => id);

    const { items } = await api.deleteCollectionsItems(itemIds);

    await dispatch({
        type: DELETE_FROM_ALL_COLLECTIONS,
        data: {
            items
        }
    });

    onUpdateCollections(getState().collections);

    for (const [id, item] of Object.entries(collectionItemMap)) {
        eventsReporter.report(EventTypes.collection.item.delete.success, {
            collection: find(collections, { id }),
            item
        });
    }
};

const getCollectionItemMap = (collections, resource) => collections.reduce((collectionItemMap, collection) => {
    collection.items.forEach((item) => {
        if (isEqualResource(resource, item)) {
            collectionItemMap[collection.id] = item;
        }
    });

    return collectionItemMap;
}, {});

export const SET_COLLECTIONS = 'SET_COLLECTIONS';
export const setCollections = (collections) => (
    {
        type: SET_COLLECTIONS,
        data: {
            collections
        }
    }
);
